import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Newspaper, Pin, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDateShort } from '@/lib/formatters';

interface NewsItem {
  kind: 'news';
  id: string;
  title: string;
  slug: string;
  pinned: boolean | null;
  published_at: string | null;
}

interface CampaignItem {
  kind: 'campaign';
  id: string;
  title: string;
  href: string;
  start_date: string | null;
}

type StripItem = NewsItem | CampaignItem;

export function NewsStrip() {
  const [items, setItems] = useState<StripItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const today = new Date().toISOString().slice(0, 10);
      const [newsRes, brandRes, dealerRes] = await Promise.all([
        supabase
          .from('news_posts')
          .select('id, title, slug, pinned, published_at')
          .eq('is_published', true)
          .order('pinned', { ascending: false })
          .order('published_at', { ascending: false })
          .limit(7),
        supabase
          .from('campaigns')
          .select('id, title, start_date')
          .eq('is_active', true)
          .or(`start_date.is.null,start_date.lte.${today}`)
          .or(`end_date.is.null,end_date.gte.${today}`)
          .order('sort_order', { ascending: true })
          .limit(4),

        supabase.rpc('get_public_campaigns'),

      ]);

      if (cancelled) return;

      const news: NewsItem[] = (newsRes.data || []).map((n: any) => ({
        kind: 'news',
        id: n.id,
        title: n.title,
        slug: n.slug,
        pinned: n.pinned,
        published_at: n.published_at,
      }));
      const brandCampaigns: CampaignItem[] = (brandRes.data || []).map((c: any) => ({
        kind: 'campaign',
        id: `brand-${c.id}`,
        title: c.title,
        href: '/kampagner',
        start_date: c.start_date ?? null,
      }));
      const dealerCampaigns: CampaignItem[] = (dealerRes.data || []).map((c: any) => ({
        kind: 'campaign',
        id: `dealer-${c.id}`,
        title: c.title,
        href: '/kampagner',
        start_date: c.start_date ?? null,
      }));

      // Interleave: pinned news first, then alternate campaigns + news
      const pinnedNews = news.filter((n) => n.pinned);
      const restNews = news.filter((n) => !n.pinned);
      const campaigns = [...brandCampaigns, ...dealerCampaigns];
      const merged: StripItem[] = [...pinnedNews];
      const maxLen = Math.max(restNews.length, campaigns.length);
      for (let i = 0; i < maxLen; i++) {
        if (restNews[i]) merged.push(restNews[i]);
        if (campaigns[i]) merged.push(campaigns[i]);
      }
      setItems(merged.slice(0, 10));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Duplicate items for seamless loop; stable ref so animation doesn't reset
  const duplicated = useMemo(() => (items.length ? [...items, ...items] : []), [items]);

  return (
    <div className="bg-primary text-primary-foreground py-2 text-sm overflow-hidden">
      <div className="flex items-center">
        <div className="flex items-center gap-2 shrink-0 pl-4 pr-4 bg-primary z-10">
          <Newspaper className="w-4 h-4 text-accent" />
          <span className="font-semibold">Nyt:</span>
        </div>

        <div className="overflow-hidden flex-1 relative min-h-[1.5rem]">
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-primary to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-primary to-transparent z-10 pointer-events-none" />

          {duplicated.length > 0 && (
            <div className="flex items-center gap-8 animate-marquee whitespace-nowrap w-max">
              {duplicated.map((item, index) => {
                if (item.kind === 'campaign') {
                  return (
                    <Link
                      key={`${item.id}-${index}`}
                      to={item.href}
                      className="flex items-center gap-2 hover:text-accent transition-colors whitespace-nowrap group shrink-0"
                    >
                      <Sparkles className="w-3 h-3 text-accent" />
                      <span className="text-accent font-semibold">Kampagne:</span>
                      <span className="group-hover:underline">{item.title}</span>
                    </Link>
                  );
                }
                return (
                  <Link
                    key={`${item.id}-${index}`}
                    to={`/nyheder/${item.slug}`}
                    className="flex items-center gap-2 hover:text-accent transition-colors whitespace-nowrap group shrink-0"
                  >
                    {item.pinned && <Pin className="w-3 h-3 text-accent" />}
                    <span className="group-hover:underline">{item.title}</span>
                    {item.published_at && (
                      <span className="text-primary-foreground/50 text-xs">
                        {formatDateShort(item.published_at)}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <Link
          to="/nyheder"
          className="hidden md:inline-flex items-center gap-1 shrink-0 pl-4 pr-4 bg-primary z-10 text-xs font-semibold text-accent hover:underline whitespace-nowrap border-l border-primary-foreground/20"
        >
          Alle nyheder <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
